import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import type { Map as MLMap, Marker, StyleSpecification } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import type { Position } from "../lib/geo";
import { IS_LITE } from "../lib/perf";

// Tile fetching + vector parsing already run on MapLibre's Web Workers;
// the only main-thread tile cost is the final WebGL texture/buffer
// upload. Cap how many image tiles are uploaded per frame so a burst of
// incoming tiles (especially the satellite + label raster layers)
// doesn't stall pan in a single frame — it spreads the work across more
// frames instead. Defaults are 16 parallel / 8 per frame.
{
  const cfg = (maplibregl as unknown as {
    config?: { MAX_PARALLEL_IMAGE_REQUESTS?: number; MAX_PARALLEL_IMAGE_REQUESTS_PER_FRAME?: number };
  }).config;
  if (cfg) {
    cfg.MAX_PARALLEL_IMAGE_REQUESTS = 12;
    cfg.MAX_PARALLEL_IMAGE_REQUESTS_PER_FRAME = IS_LITE ? 2 : 4;
  }
}

export type TrafficMarker = {
  id: string;
  lat: number;
  lon: number;
  incidentType: string;
  text: string;
  concluded: boolean;
};

type Props = {
  position: Position | null;
  accentHex: string;
  /** "night" → CartoDB Dark Matter GL; "day" → Voyager GL. */
  theme: "day" | "night";
  /** When true, swap the vector basemap for satellite/aerial imagery. */
  satellite: boolean;
  /** GeoJSON LineString geometry for the active route, or null. */
  routeGeometry: { type: "LineString"; coordinates: [number, number][] } | null;
  /** Destination pin, drawn at the route's end. */
  destination: { lat: number; lon: number } | null;
  /** Live traffic incidents (DR Trafik). Concluded items are filtered out client-side. */
  trafficMarkers: TrafficMarker[];
};

// Vector tile basemaps. Both are free, no API key required:
//   - CartoDB Positron / Dark Matter GL styles, served from basemaps.cartocdn.com
//   - The same OpenMapTiles schema underneath, so the visual feel matches the
//     raster CartoDB tiles we used with Leaflet.
// MapLibre rasterizes vector tiles into a single GL canvas — Chromium's
// compositor no longer has to juggle hundreds of <img> tiles, which is the
// architectural fix for the Android WebView "tile memory limits exceeded"
// problem we were hitting with Leaflet.
const STYLE_URLS: Record<"day" | "night", string> = {
  // Voyager (not Positron) for day — same modern CartoDB family but with
  // far better street contrast: white roads with casing, gentle colour
  // for water/parks/landuse, so streets read clearly at a glance.
  day: "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json",
  night: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
};

/**
 * Satellite / aerial imagery style, built inline as a raster style.
 *   - ESRI World Imagery as the base (free, no API key, CORS-enabled).
 *   - CartoDB "dark_only_labels" raster overlaid for road + place names
 *     (white text reads cleanly over imagery; no glyphs/sprite needed
 *     since it's a raster, keeping the style self-contained).
 * Returns a full MapLibre StyleSpecification so the route/trail/marker
 * layers get re-added on top by the styledata handler, same as the
 * vector styles.
 */
function satelliteStyle(): StyleSpecification {
  const sfx = IS_LITE ? "" : "@2x";
  const carto = (s: string) =>
    ["a", "b", "c", "d"].map((d) => `https://${d}.basemaps.cartocdn.com/${s}/{z}/{x}/{y}${sfx}.png`);
  return {
    version: 8,
    sources: {
      sat: {
        type: "raster",
        tiles: ["https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"],
        tileSize: 256,
        maxzoom: 19,
        attribution: "Imagery © Esri, Maxar, Earthstar Geographics",
      },
      labels: {
        type: "raster",
        tiles: carto("dark_only_labels"),
        tileSize: 256,
        maxzoom: 20,
        attribution: "© OpenStreetMap, © CARTO",
      },
    },
    layers: [
      { id: "bg", type: "background", paint: { "background-color": "#0a0c12" } },
      { id: "sat", type: "raster", source: "sat" },
      { id: "labels", type: "raster", source: "labels", paint: { "raster-opacity": 0.92 } },
    ],
  };
}

function styleFor(theme: "day" | "night", satellite: boolean): string | StyleSpecification {
  return satellite ? satelliteStyle() : STYLE_URLS[theme];
}

/**
 * Speed (km/h) → target zoom level. MapLibre supports fractional zooms
 * natively (no zoomSnap thrash like Leaflet), so we can lerp freely.
 */
const ZOOM_CURVE: ReadonlyArray<readonly [number, number]> = [
  [0, 17.8],   // standstill / parking — close, almost house level
  [15, 17.2],  // residential crawl
  [35, 16.5],  // city slow
  [60, 15.7],  // arterial
  [90, 14.6],  // dual carriageway
  [120, 13.5], // motorway
  [180, 13],   // high speed
];
function zoomForSpeed(speedKmh: number): number {
  const pts = ZOOM_CURVE;
  if (speedKmh <= pts[0][0]) return pts[0][1];
  if (speedKmh >= pts[pts.length - 1][0]) return pts[pts.length - 1][1];
  for (let i = 0; i < pts.length - 1; i++) {
    const [s0, z0] = pts[i];
    const [s1, z1] = pts[i + 1];
    if (speedKmh >= s0 && speedKmh <= s1) {
      const t = (speedKmh - s0) / (s1 - s0);
      const e = t * t * (3 - 2 * t);
      return z0 + (z1 - z0) * e;
    }
  }
  return pts[pts.length - 1][1];
}

const RESUME_DELAY_MS = 8_000;

/** Shortest signed arc from `current` to `target` in degrees (-180, 180]. */
function shortestArc(target: number, current: number): number {
  let d = target - current;
  d = ((d % 360) + 540) % 360 - 180;
  return d;
}

function incidentClass(type: string): string {
  const t = type.toUpperCase();
  if (t.includes("ACCIDENT")) return "accident";
  if (t.includes("ROADWORK") || t.includes("WORK")) return "roadwork";
  if (t.includes("CLOSE") || t.includes("BLOCK")) return "closed";
  if (t.includes("WEATHER") || t.includes("SNOW") || t.includes("ICE")) return "weather";
  if (t.includes("WARN")) return "warning";
  return "other";
}

function incidentLabel(type: string): string {
  const t = type.toUpperCase();
  if (t.includes("ACCIDENT")) return "Uheld";
  if (t.includes("ROADWORK") || t.includes("WORK")) return "Vejarbejde";
  if (t.includes("CLOSE") || t.includes("BLOCK")) return "Vejen er spærret";
  if (t.includes("WEATHER")) return "Vejret";
  if (t.includes("SNOW") || t.includes("ICE")) return "Glat vej";
  if (t.includes("WARN")) return "Advarsel";
  return "Trafikmelding";
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

const ROUTE_SRC = "cardash-route";
const ROUTE_OUTLINE_LYR = "cardash-route-outline";
const ROUTE_FILL_LYR = "cardash-route-fill";
const TRAIL_SRC = "cardash-trail";
const TRAIL_LYR = "cardash-trail";

function emptyLineFC(): GeoJSON.FeatureCollection<GeoJSON.LineString> {
  return { type: "FeatureCollection", features: [] };
}

export function MapView({ position, accentHex, theme, satellite, routeGeometry, destination, trafficMarkers }: Props) {
  const mapRef = useRef<MLMap | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const outerRef = useRef<HTMLDivElement | null>(null);
  const carMarkerRef = useRef<Marker | null>(null);
  const destMarkerRef = useRef<Marker | null>(null);
  const trafficMarkersRef = useRef<Marker[]>([]);
  const trailPointsRef = useRef<[number, number][]>([]);
  // Tracks whether the last position was simulated, so we can clear the
  // breadcrumb trail on the sim→real GPS handoff.
  const prevSimulatedRef = useRef(false);
  const styleReadyRef = useRef(false);
  const lastBearingRef = useRef(0);
  // Smoothed zoom — tracks the speed-derived target via an exp filter so
  // the zoom never "jumps". Passed into `jumpTo` alongside center/bearing.
  const smoothZoomRef = useRef(16);

  // User-interaction tracking
  const interactingRef = useRef(false);
  const resumeTimerRef = useRef<number | null>(null);
  const lastPosRef = useRef<Position | null>(null);
  lastPosRef.current = position;
  const flyToActiveUntilRef = useRef(0);

  // ─── INIT ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!wrapRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: wrapRef.current,
      style: styleFor(theme, satellite),
      center: [12.5683, 55.6761],
      zoom: 16,
      bearing: 0,
      pitch: 0,
      attributionControl: { compact: true },
      // No fade-in for incoming tiles — under heading-up rotation, new
      // tiles arrive continuously; the fade adds compositor blending on
      // top of every redraw and visibly stalls panning. Snapping in is
      // fine for our use case.
      fadeDuration: 0,
      // Tile cache size — bumped above MapLibre's tiny default so the
      // viewport's tiles stay resident as we pan, but kept conservative
      // for Android WebView's GPU budget. 512 caused long-drive memory
      // pressure on the head unit (cache filled with new tiles every
      // few minutes of new geography → eventual UI freeze). 192 covers
      // the visible area + a couple of viewports of slack.
      maxTileCacheSize: 192,
      // Cache one zoom level above + below the current — enough so the
      // adaptive speed-zoom doesn't immediately refetch on every change.
      maxTileCacheZoomLevels: 3,
      // No keyboard / right-click / cooperative gestures.
      keyboard: false,
    });
    mapRef.current = map;

    // Car marker (DOM-based) — pulsing circle anchored at position.lat/lon.
    const carEl = document.createElement("div");
    carEl.className = "car-marker";
    carMarkerRef.current = new maplibregl.Marker({
      element: carEl,
      // viewport: keep upright on screen regardless of map bearing. The
      // marker is a circle so it doesn't need to point a direction; the
      // map being heading-up already conveys travel direction = screen up.
      rotationAlignment: "viewport",
      pitchAlignment: "viewport",
    })
      .setLngLat([12.5683, 55.6761])
      .addTo(map);

    // ── Style is loaded asynchronously. Add sources/layers in `load`.
    map.on("load", () => {
      styleReadyRef.current = true;
      ensureRouteLayer(map, accentHex);
      ensureTrailLayer(map, accentHex);
      applyRouteGeometry(map, routeGeometry);
    });

    // ── User-interaction pause: drag/zoom defer auto-follow.
    const onInteractStart = () => {
      interactingRef.current = true;
      if (resumeTimerRef.current) {
        clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = null;
      }
      outerRef.current?.classList.add("is-user-panning");
      // Switch to north-up while the user is dragging — the CSS
      // rotation transform makes drag direction inconsistent with
      // screen direction otherwise. Snap to the nearest 0-equivalent
      // (multiple of 360) of the continuous accumulator so the tween
      // takes the short way, not a full spin back through 0.
      const northUp = lastBearingRef.current + shortestArc(0, lastBearingRef.current);
      lastBearingRef.current = northUp;
      document.documentElement.style.setProperty("--map-rotation", `${-northUp}deg`);
    };
    const scheduleResume = () => {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = window.setTimeout(() => {
        interactingRef.current = false;
        outerRef.current?.classList.remove("is-user-panning");
        const cur = lastPosRef.current;
        if (cur && mapRef.current) {
          const z = zoomForSpeed((cur.speedMs ?? 0) * 3.6);
          const flyDur = IS_LITE ? 600 : 1400;
          // Park per-frame jumpTo until the easeTo finishes — otherwise
          // the next position update's jumpTo would cancel it.
          flyToActiveUntilRef.current = Date.now() + flyDur + 100;
          // Seed the continuous accumulator to the nearest equivalent of
          // the current heading so the resume eases back to heading-up
          // the short way (the CSS transition tweens it).
          const heading = cur.heading != null ? cur.heading : 0;
          const accum = lastBearingRef.current + shortestArc(heading, lastBearingRef.current);
          lastBearingRef.current = accum;
          smoothZoomRef.current = z;
          document.documentElement.style.setProperty("--map-rotation", `${-accum}deg`);
          mapRef.current.easeTo({
            center: [cur.lon, cur.lat],
            zoom: z,
            duration: flyDur,
            easing: (t) => t * (2 - t),
          });
        }
      }, RESUME_DELAY_MS);
    };

    // MapLibre fires dragstart/zoomstart for both user and programmatic
    // moves. Filter on the originalEvent — programmatic easeTo has none.
    map.on("dragstart", (e) => { if (e.originalEvent) onInteractStart(); });
    map.on("zoomstart", (e) => { if (e.originalEvent) onInteractStart(); });
    map.on("rotatestart", (e) => { if (e.originalEvent) onInteractStart(); });
    map.on("dragend", (e) => { if (e.originalEvent) scheduleResume(); });
    map.on("zoomend", (e) => { if (interactingRef.current && e.originalEvent) scheduleResume(); });
    map.on("rotateend", (e) => { if (interactingRef.current && e.originalEvent) scheduleResume(); });

    // ResizeObserver — handles late layout (Vite CSS injection, parent
    // resize). MapLibre also listens to window resize internally so we
    // only need to nudge it on container size changes.
    const ro = new ResizeObserver(() => mapRef.current?.resize());
    ro.observe(wrapRef.current);

    return () => {
      ro.disconnect();
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
      trafficMarkersRef.current.forEach((m) => m.remove());
      trafficMarkersRef.current = [];
      carMarkerRef.current?.remove();
      carMarkerRef.current = null;
      destMarkerRef.current?.remove();
      destMarkerRef.current = null;
      map.remove();
      mapRef.current = null;
      styleReadyRef.current = false;
    };
  }, []);

  // ─── STYLE SWAP (theme or satellite) ─────────────────────────────────
  // Skip the first run: the map was constructed with the correct style
  // already, so re-calling setStyle on mount just causes a redundant
  // reload (and a flash). Only swap on actual theme/satellite changes.
  const styleMountedRef = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!styleMountedRef.current) { styleMountedRef.current = true; return; }
    styleReadyRef.current = false;
    map.setStyle(styleFor(theme, satellite));
    map.once("styledata", () => {
      styleReadyRef.current = true;
      ensureRouteLayer(map, accentHex);
      ensureTrailLayer(map, accentHex);
      applyRouteGeometry(map, routeGeometry);
      applyTrailGeometry(map, trailPointsRef.current);
    });
  }, [theme, satellite]);

  // ─── ACCENT COLOR ON ROUTE + TRAIL ───────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReadyRef.current) return;
    if (map.getLayer(ROUTE_FILL_LYR)) map.setPaintProperty(ROUTE_FILL_LYR, "line-color", accentHex);
    if (map.getLayer(TRAIL_LYR)) map.setPaintProperty(TRAIL_LYR, "line-color", accentHex);
  }, [accentHex]);

  // ─── ROUTE GEOMETRY ──────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReadyRef.current) return;
    applyRouteGeometry(map, routeGeometry);
  }, [routeGeometry]);

  // ─── DESTINATION MARKER ──────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (destMarkerRef.current) {
      destMarkerRef.current.remove();
      destMarkerRef.current = null;
    }
    if (!destination) return;
    const el = document.createElement("div");
    el.className = "dest-marker";
    el.innerHTML = '<div class="dest-pin"></div><div class="dest-stem"></div>';
    destMarkerRef.current = new maplibregl.Marker({
      element: el,
      anchor: "bottom",
      rotationAlignment: "viewport",
      pitchAlignment: "viewport",
    })
      .setLngLat([destination.lon, destination.lat])
      .addTo(map);
  }, [destination?.lat, destination?.lon]);

  // ─── TRAFFIC MARKERS ─────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Tear down previous batch.
    trafficMarkersRef.current.forEach((m) => m.remove());
    trafficMarkersRef.current = [];
    // Skip entirely in lite mode — dozens of DOM markers still cost
    // compositor work even with the GL basemap.
    if (IS_LITE) return;
    if (!trafficMarkers.length) return;

    for (const t of trafficMarkers) {
      const el = document.createElement("div");
      el.className = `traffic-marker traffic-${incidentClass(t.incidentType)}${t.concluded ? " concluded" : ""}`;
      el.innerHTML = '<span class="traffic-marker-dot"></span>';
      const popup = new maplibregl.Popup({
        closeButton: true,
        closeOnClick: true,
        maxWidth: "320px",
        offset: 14,
      }).setHTML(
        `<div class="traffic-popup"><div class="traffic-popup-type">${incidentLabel(t.incidentType)}</div>` +
        `<div class="traffic-popup-text">${escapeHtml(t.text)}</div></div>`,
      );
      const m = new maplibregl.Marker({
        element: el,
        rotationAlignment: "viewport",
        pitchAlignment: "viewport",
      })
        .setLngLat([t.lon, t.lat])
        .setPopup(popup)
        .addTo(map);
      trafficMarkersRef.current.push(m);
    }
  }, [trafficMarkers]);

  // ─── FOLLOW POSITION + HEADING-UP + ADAPTIVE ZOOM ────────────────────
  // Pan + rotation are driven by direct `jumpTo` per position update —
  // it's a single transform-matrix update on the GL canvas, cheaper than
  // calling `easeTo` which spawns its own RAF animation and gets
  // interrupted by the next call. The simulator already interpolates the
  // car position smoothly, so we don't need extra tweening on top.
  //
  // Zoom is the exception: it changes slowly with speed, and a sudden
  // jumpTo would feel jarring. Keep `easeTo` for that, throttled.
  useEffect(() => {
    const map = mapRef.current;
    const car = carMarkerRef.current;
    if (!map || !car || !position) return;

    car.setLngLat([position.lon, position.lat]);

    // Drop the simulated trail the instant we switch to a real GPS fix —
    // otherwise the breadcrumb draws a long line from the simulator's
    // Copenhagen loop across to wherever the device actually is.
    const isSim = !!position.simulated;
    if (prevSimulatedRef.current && !isSim) {
      trailPointsRef.current.length = 0;
      if (styleReadyRef.current) applyTrailGeometry(map, []);
    }
    prevSimulatedRef.current = isSim;

    // Append to trail (skip in lite mode). 12 m threshold (was 5 m) —
    // every setData on the trail source re-tessellates the line and
    // re-uploads the GL buffer. Re-doing that while the map is also
    // rotating + panning is what tips the frame budget over. 12 m at
    // ~50 km/h ≈ once per second, plenty for a visible breadcrumb.
    if (!IS_LITE && styleReadyRef.current) {
      const pts = trailPointsRef.current;
      const last = pts[pts.length - 1];
      // A big single-step jump (>~250 m) means a discontinuity — GPS
      // teleport or the sim→real handoff — so start the trail fresh
      // rather than drawing a line across the gap.
      if (last && Math.hypot(last[0] - position.lon, last[1] - position.lat) > 0.0025) {
        pts.length = 0;
      }
      const tail = pts[pts.length - 1];
      const distOk = !tail || Math.hypot(tail[0] - position.lon, tail[1] - position.lat) > 0.00012;
      if (distOk) {
        pts.push([position.lon, position.lat]);
        while (pts.length > 200) pts.shift();
        applyTrailGeometry(map, pts);
      }
    }

    if (interactingRef.current) return;
    const now = Date.now();
    if (now < flyToActiveUntilRef.current) return;

    // Heading-up rotation is done via a CSS transform on the wrap
    // element, NOT MapLibre's bearing. Every setBearing call
    // invalidates tile coverage and re-runs symbol collision detection
    // for all visible labels — that's the irreducible cost that stalls
    // pan whenever heading changes. Keeping MapLibre at bearing 0
    // means tile coverage and label placement stay stable; only the
    // composite CSS transform changes per frame (GPU-only, basically
    // free). Trade-off: labels rotate with the map (so they can read
    // upside-down on south headings) — a known nav-map convention.
    if (position.heading != null && !IS_LITE) {
      const prev = lastBearingRef.current;
      // Accumulate CONTINUOUSLY — do NOT wrap to [0,360). Each step is
      // the shortest arc toward the heading, so leaving the running
      // value unbounded keeps consecutive --map-rotation values close.
      // The CSS `rotate()` transition interpolates the raw number, so a
      // wrap across 0/360 would make it spin the long way (180° instead
      // of 90°). shortestArc handles an unbounded `prev` fine.
      const curr = prev + shortestArc(position.heading, prev) * 0.25;
      lastBearingRef.current = curr;
      // Map rotates by -heading so the heading direction faces "up"
      // on screen. The CSS var is read by .maplibre-wrap and any
      // marker inner-elements that need to stay screen-upright.
      document.documentElement.style.setProperty("--map-rotation", `${-curr}deg`);
    }

    // Smooth zoom in JS so we can ship it inside the same jumpTo.
    const targetZoom = zoomForSpeed((position.speedMs ?? 0) * 3.6);
    smoothZoomRef.current += (targetZoom - smoothZoomRef.current) * 0.06;

    // Center + zoom go via jumpTo. MapLibre stays at bearing 0 so this
    // is the cheap per-frame path: tile coverage is the same axis-
    // aligned rectangle every frame, just centered on a new point.
    map.jumpTo({
      center: [position.lon, position.lat],
      zoom: smoothZoomRef.current,
    });
  }, [position]);

  return (
    <div ref={outerRef} className="map-layer">
      <div ref={wrapRef} className="maplibre-wrap" />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// helpers — source + layer setup. Pulled out of the component so theme
// swaps can re-create them after setStyle wipes the canvas.
// ───────────────────────────────────────────────────────────────────────

function ensureRouteLayer(map: MLMap, accent: string) {
  if (!map.getSource(ROUTE_SRC)) {
    map.addSource(ROUTE_SRC, { type: "geojson", data: emptyLineFC() });
  }
  if (!map.getLayer(ROUTE_OUTLINE_LYR)) {
    map.addLayer({
      id: ROUTE_OUTLINE_LYR,
      type: "line",
      source: ROUTE_SRC,
      paint: {
        "line-color": "#000",
        "line-width": 10,
        "line-opacity": 0.4,
      },
      layout: { "line-cap": "round", "line-join": "round" },
    });
  }
  if (!map.getLayer(ROUTE_FILL_LYR)) {
    map.addLayer({
      id: ROUTE_FILL_LYR,
      type: "line",
      source: ROUTE_SRC,
      paint: {
        "line-color": accent,
        "line-width": 6,
        "line-opacity": 0.95,
      },
      layout: { "line-cap": "round", "line-join": "round" },
    });
  }
}

function ensureTrailLayer(map: MLMap, accent: string) {
  if (IS_LITE) return;
  if (!map.getSource(TRAIL_SRC)) {
    map.addSource(TRAIL_SRC, { type: "geojson", data: emptyLineFC() });
  }
  if (!map.getLayer(TRAIL_LYR)) {
    // Trail under the route fill but above the basemap.
    const beforeId = map.getLayer(ROUTE_OUTLINE_LYR) ? ROUTE_OUTLINE_LYR : undefined;
    map.addLayer({
      id: TRAIL_LYR,
      type: "line",
      source: TRAIL_SRC,
      paint: {
        "line-color": accent,
        "line-width": 4,
        "line-opacity": 0.65,
      },
      layout: { "line-cap": "round", "line-join": "round" },
    }, beforeId);
  }
}

function applyRouteGeometry(
  map: MLMap,
  geom: { type: "LineString"; coordinates: [number, number][] } | null,
) {
  const src = map.getSource(ROUTE_SRC) as maplibregl.GeoJSONSource | undefined;
  if (!src) return;
  if (!geom) {
    src.setData(emptyLineFC());
    return;
  }
  src.setData({
    type: "FeatureCollection",
    features: [{ type: "Feature", geometry: geom, properties: {} }],
  });
}

function applyTrailGeometry(map: MLMap, pts: [number, number][]) {
  const src = map.getSource(TRAIL_SRC) as maplibregl.GeoJSONSource | undefined;
  if (!src) return;
  if (pts.length < 2) {
    src.setData(emptyLineFC());
    return;
  }
  src.setData({
    type: "FeatureCollection",
    features: [{
      type: "Feature",
      geometry: { type: "LineString", coordinates: pts },
      properties: {},
    }],
  });
}

